/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { PluginsNotificationService } from 'plugins-notification/lib/plugins-notification.service';
import { LoggingService } from '../logging/logging.service';
import { BridgeMockService } from './bridge.mocks.service';
import { BridgeService } from './bridge.service';
import { FullBridgeCommand } from './bridge.service';

@Injectable()
export class CefService extends BridgeService {
    private static EXTENSION_MESSAGE = 'QUERY_IPC_EXTENSION_MESSAGE';

    constructor(
        loggingService: LoggingService,
        private pluginsNotificationService: PluginsNotificationService,
        private ngZone: NgZone,
        bridgeMocks?: BridgeMockService
    ) {
        super(loggingService.getLogger('crimson/cef'), bridgeMocks);
        this.pluginsNotificationService.init(this.executeCommand.bind(this));
    }

    static isCefPlatform(): boolean {
        return 'cefQuery' in window;
    }

    executeCommandInternal<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean
    ): Observable<ResponseType> {
        const fullBridgeCommand: FullBridgeCommand<ParamType> = this.makeFullBridgeCommand(
            command,
            params,
            command.legacyCommand !== CefService.EXTENSION_MESSAGE
        );
        const request: string = JSON.stringify(fullBridgeCommand);
        const cefQuery = (window as any).cefQuery;
        const checkPluginLoad$ = this.pluginsNotificationService.isMandatoryPlugin(command.module)
            ? this.pluginsNotificationService.isPluginLoaded$(command.module)
            : of(true);
        return new Observable(observer => {
            if (cefQuery) {
                // Blocking calls for mandatory plugins if not loaded
                checkPluginLoad$
                    .pipe(
                        filter(value => value),
                        take(1)
                    )
                    .subscribe(_ => {
                        cefQuery({
                            request,
                            persistent: command.persistent,
                            onSuccess: (response: any) => {
                                // Especially in the case of Android clients we call out to pure javascript and it returns
                                // outside the angular zone when a response is received back. This causes errors like the UI
                                // not updating itself until another digest cycle is triggered (like a tap). Here we
                                // check to make sure that we are in an angular zone before calling back to the observer
                                // so it can be processed in the normal angular lifecycle.
                                if (NgZone.isInAngularZone()) {
                                    this.handleSuccess<ResponseType>(observer, command.persistent, response);
                                } else {
                                    this.ngZone.run(() => {
                                        this.handleSuccess<ResponseType>(observer, command.persistent, response);
                                    });
                                }
                            },
                            onFailure: (errorCode: number, errorMessage: string) => {
                                if (NgZone.isInAngularZone()) {
                                    this.handleFailure(
                                        observer,
                                        fullBridgeCommand,
                                        errorCode,
                                        errorMessage,
                                        shouldLogPayload,
                                        shouldIncludePayloadInError
                                    );
                                } else {
                                    this.ngZone.run(() => {
                                        this.handleFailure(
                                            observer,
                                            fullBridgeCommand,
                                            errorCode,
                                            errorMessage,
                                            shouldLogPayload,
                                            shouldIncludePayloadInError
                                        );
                                    });
                                }
                            }
                        });
                    });
            } else {
                const errorLog = 'Not running on CEF. Command: ' + shouldLogPayload ? request : '';
                this.logger.error(errorLog);
                this.handleFailure(
                    observer,
                    fullBridgeCommand,
                    500,
                    errorLog,
                    shouldLogPayload,
                    shouldIncludePayloadInError
                );
            }
        });
    }
}
